import _ from 'underscore';

import * as benchlingUtil from 'BenchlingUtil';
import * as BioJS from 'BioJS';
import {CODON_LENGTH, CodonTranslationTable} from 'biojs/BioConstants';
import {Sequence} from 'components/Sequence';
import {Strand} from 'constants/MolbioConstants';
import {
  ColoredAminoAcidSLA,
  getAminoAcidBackgroundColor,
  getAminoAcidGlyphColor,
  ProteinColorScheme,
} from 'molbio/aa-sequences/util/AaSequenceColoringConstants';
import * as selectables from 'molbio/maps/selectables/Selectables';
import {AminoAcid} from 'molbio/MolBioConstants';
import * as sequtil from 'molbio/SeqUtil';

function lengthAllowEmpty(start: number, end: number, seqLength: number): number {
  if (start === end) {
    return 0;
  }
  return sequtil.length(start, end, seqLength);
}

abstract class BaseTranslation extends selectables.RegionSelectable<Sequence> {
  abstract override strand(): Strand;

  translationGroupId(): number | string | null {
    return null;
  }

  codonTranslationTable(sequence: Sequence): CodonTranslationTable {
    return CodonTranslationTable.STANDARD;
  }

  protected coerceStartCodonToMethionine(sequence: Sequence): boolean {
    return false;
  }

  groupTranslations(): Array<typeof this> {
    if (this.translationGroupId() == null) {
      return [this];
    }
    return this.collection.filter((t) => t.translationGroupId() === this.translationGroupId());
  }

  aminoAcidLength(sequence: Sequence): number {
    if (!this.translationGroupId()) {
      return Math.floor(this.length(sequence) / CODON_LENGTH);
    }

    // Return the number of "3rd" bases in the translation segment.
    // i.e. if the translation group looked like: [AAGA]...[AGAA].. [GAAG]
    // the aminoAcidLengths would be 1, 1, 2 (G is the third base).
    const upstreamLength = benchlingUtil.sum(this.upstreamGroupTranslations().map((t) => t.length(sequence)));
    return Math.floor((this.length(sequence) + (upstreamLength % CODON_LENGTH)) / CODON_LENGTH);
  }

  totalGroupLength(sequence: Sequence): number {
    return benchlingUtil.sum(this.groupTranslations().map((translation) => translation.length(sequence)));
  }

  leftGroupTranslations(): Array<BaseTranslation> {
    return this.groupTranslations().filter((t) => t.start() < this.start());
  }

  rightGroupTranslations(): Array<BaseTranslation> {
    return this.groupTranslations().filter((t) => t.start() > this.start());
  }

  upstreamGroupTranslations(): Array<BaseTranslation> {
    if (this.strand() === 1) {
      return this.leftGroupTranslations();
    } else {
      return this.rightGroupTranslations();
    }
  }

  getAminoAcids(
    sequence: Sequence,
    sliceStart: number,
    sliceEnd: number,
    colorScheme: ProteinColorScheme = ProteinColorScheme.RASMOL
  ): Array<AminoAcid> {
    let computeIndexStart: (index: number) => number;
    let processAminoAcids;

    if (sequence.length() < sliceEnd) {
      // When making edits to a sequence, sometimes Backbone listeners fire *before* everything is up to
      // date. This causes the sliceEnd to be temporarily greater than the sequence length, which can cause
      // residueSlice to give a noisy warning. Instead, we render nothing temporarily, and once everything is
      // in sync, this method *should* be be called with correct parameters. Ref T5236.
      return [];
    }

    // TODO(T5646): Refactor this into a utility function.
    if (sequence.length() === 0) {
      return [];
    }

    const _sequence = sequence._sequence();
    const isGapSequence = _sequence instanceof BioJS.GapSequence;

    function posToReal(pos: number): number {
      if (isGapSequence) {
        return _sequence.gapToReal(pos);
      } else {
        return pos;
      }
    }

    function realToPos(real: number): number {
      if (isGapSequence) {
        return _sequence.realToGap(real);
      } else {
        return real;
      }
    }

    function realEndToPos(real: number): number {
      if (isGapSequence) {
        return _sequence.realEndToGap(real);
      } else {
        return real;
      }
    }

    const rStart = posToReal(this.start());
    const rEnd = posToReal(this.end());
    const rSliceStart = posToReal(sliceStart);
    const rSliceEnd = posToReal(sliceEnd);
    const rLength = posToReal(sequence.length());

    const nonGapBases = BioJS.util.removeGaps(sequence.residueSlice(this.start(), this.end()));
    if (rLength === 0 || nonGapBases.length === 0) {
      // If the sequence is empty, or the translation only spans dashes
      return [];
    }

    function realTranslationLength(translation: BaseTranslation): number {
      if (translation.start() === translation.end()) {
        return posToReal(sequence.length());
      }
      // If the realStart = realEnd, but start != end, this translation is just over gaps in an alignment.
      return lengthAllowEmpty(posToReal(translation.start()), posToReal(translation.end()), rLength);
    }

    // Returns the number of padding bases needed on either side of the slice
    // This is for slices that are not in frame with the translation, and translations that are in groups.
    const getBasesNeeded = (
      rBaseStart: number,
      rBaseEnd: number
    ): {startBasesNeeded: number; endBasesNeeded: number} => {
      const groupLength = benchlingUtil.sum(
        this.upstreamGroupTranslations().map((t) => realTranslationLength(t))
      );
      const slicedLength = sequtil.length(rBaseStart, rBaseEnd, rLength);
      let startBasesNeeded;
      let endBasesNeeded;
      if (this.strand() === 1) {
        // realStart == realBaseStart means no bases in between, it will never be a full sequence length.
        startBasesNeeded = (groupLength + lengthAllowEmpty(rStart, rBaseStart, rLength)) % CODON_LENGTH;
        endBasesNeeded = sequtil.mod(CODON_LENGTH - (startBasesNeeded + slicedLength), CODON_LENGTH);
      } else {
        // realEnd == realBaseEnd means no bases in between, it will never be a full sequence length.
        endBasesNeeded = (groupLength + lengthAllowEmpty(rBaseEnd, rEnd, rLength)) % CODON_LENGTH;
        startBasesNeeded = sequtil.mod(CODON_LENGTH - (endBasesNeeded + slicedLength), CODON_LENGTH);
      }
      return {startBasesNeeded, endBasesNeeded};
    };

    const getAAIndexStart = (
      rStart: number,
      rEnd: number,
      rBaseStart: number,
      rBaseEnd: number,
      rLength: number
    ): number => {
      let basesLength;
      const groupLength = benchlingUtil.sum(
        this.upstreamGroupTranslations().map((t) => realTranslationLength(t))
      );
      if (this.strand() === 1) {
        // realStart == realBaseStart means no bases in between, it will never be a full sequence length.
        basesLength = lengthAllowEmpty(rStart, rBaseStart, rLength);
      } else {
        // realEnd == realBaseEnd means no bases in between, it will never be a full sequence length.
        basesLength = lengthAllowEmpty(rBaseEnd, rEnd, rLength);
      }
      return 1 + Math.floor((groupLength + basesLength) / CODON_LENGTH);
    };

    // Given a real start and end positions, returns the real bases inside
    function realBaseSlice(rSequenceStart: number, rSequenceEnd: number, allowEmpty?: boolean): string {
      const sequenceStart = realToPos(rSequenceStart);
      const sequenceEnd = realEndToPos(rSequenceEnd);
      return BioJS.util.removeGaps(sequence.residueSlice(sequenceStart, sequenceEnd, allowEmpty));
    }

    // Returns the bases to pad the left side of the slice with
    const getStartBases = (rBaseStart: number, numBases: number): string => {
      // realStart == realBaseStart means no bases in between, it will never be a full sequence length.
      let startBases;
      const selfNumBases = Math.min(numBases, lengthAllowEmpty(rStart, rBaseStart, rLength));

      // We special case selfNumBases == 0 because realBaseSlice does not handle the edge case of
      // rSequenceStart == rSequenceEnd == 0 because of realToPos vs realEndToPos.
      if (selfNumBases === 0) {
        startBases = '';
      } else {
        const selfStart = sequtil.mod(rBaseStart - selfNumBases, rLength);
        numBases -= selfNumBases;
        startBases = realBaseSlice(selfStart, rBaseStart, true);
      }

      if (numBases !== 0) {
        const translations = _.sortBy(this.leftGroupTranslations(), (t) => -t.start());
        let index = 0;
        let translation;
        let tBases;
        while (numBases > 0 && index < translations.length) {
          translation = translations[index++];
          const rTransEnd = posToReal(translation.end());
          const rTransStart = posToReal(translation.start());
          // These translations can never span the full sequence, since group translations shouldn't overlap.
          const tNumBases = Math.min(numBases, lengthAllowEmpty(rTransStart, rTransEnd, rLength));
          tBases = realBaseSlice(sequtil.mod(rTransEnd - tNumBases, rLength), rTransEnd, true);
          startBases = tBases + startBases;
          numBases -= tBases.length;
          index += 1;
        }
      }

      return startBases;
    };

    // Returns the bases to pad the right side of the slice with
    const getEndBases = (rBaseEnd: number, numBases: number): string => {
      // rBaseEnd == rEnd means no bases in between, it will never be a full sequence length.
      let endBases;
      const selfNumBases = Math.min(numBases, lengthAllowEmpty(rBaseEnd, rEnd, rLength));

      // We special case selfNumBases == 0 because realBaseSlice does not handle the edge case of
      // rBaseEnd == rLength, selfEnd == 0 because of realToPos vs realEndToPos.
      if (selfNumBases === 0) {
        endBases = '';
      } else {
        const selfEnd = sequtil.mod(rBaseEnd + selfNumBases, rLength);
        numBases -= selfNumBases;
        endBases = realBaseSlice(rBaseEnd, selfEnd, true);
      }

      if (numBases !== 0) {
        const translations = _.sortBy(this.rightGroupTranslations(), (t) => t.start());
        let index = 0;
        let translation;
        let tBases;
        while (numBases > 0 && index < translations.length) {
          translation = translations[index++];
          const rTransStart = posToReal(translation.start());
          const rTransEnd = posToReal(translation.end());
          // These translations can never span the full sequence, since group translations shouldn't overlap.
          const tNumBases = Math.min(numBases, lengthAllowEmpty(rTransStart, rTransEnd, rLength));
          tBases = realBaseSlice(rTransStart, sequtil.mod(rTransStart + tNumBases, rLength), true);
          endBases += tBases;
          numBases -= tBases.length;
        }
      }
      return endBases;
    };

    // rBaseStart and rBaseEnd should always be 0 <= rBaseEnd, rBaseStart < rLength
    const rBaseStart = sequtil.baseInRange(rStart, rSliceStart, rSliceEnd) ? rStart : rSliceStart;
    const rBaseEnd = sequtil.endInRange(rEnd, rSliceStart, rSliceEnd, rLength)
      ? rEnd
      : sequtil.mod(rSliceEnd, rLength);

    const {startBasesNeeded, endBasesNeeded} = getBasesNeeded(rBaseStart, rBaseEnd);

    const realStartBases = getStartBases(rBaseStart, startBasesNeeded);
    const realEndBases = getEndBases(rBaseEnd, endBasesNeeded);

    // If rBaseStart == rBaseEnd, it means that the translation spans the entire sequence.
    const realMiddleBases = realBaseSlice(rBaseStart, rBaseEnd);
    const bases = realStartBases + realMiddleBases + realEndBases;

    const aaIndexStart = getAAIndexStart(rStart, rEnd, rBaseStart, rBaseEnd, rLength);
    if (this.strand() === 1) {
      computeIndexStart = (index) =>
        sequtil.mod(index * CODON_LENGTH + rBaseStart - realStartBases.length, rLength);
      processAminoAcids = (aminoAcids: Array<AminoAcid>) => aminoAcids;
    } else {
      computeIndexStart = (index) =>
        sequtil.mod(rBaseEnd + realEndBases.length - (index + 1) * CODON_LENGTH, rLength);
      processAminoAcids = (aminoAcids: Array<AminoAcid>) => aminoAcids.reverse();
    }

    return processAminoAcids(
      BioJS.util
        .translateOnStrand(
          bases,
          this.strand(),
          sequence.nucleotideType(),
          this.codonTranslationTable(sequence),
          // Only coerce when this is the first slice of a translation region, since it could be
          // a region that renders over multiple slices.
          this.coerceStartCodonToMethionine(sequence) && aaIndexStart === 1
        )
        .split('')
        .map((b: ColoredAminoAcidSLA, index) => {
          let indexStart = computeIndexStart(index);
          let indexEnd = sequtil.mod(indexStart + CODON_LENGTH, rLength);
          const aaIndex = aaIndexStart + index;
          const backgroundColor = getAminoAcidBackgroundColor(b, index, colorScheme);
          const glyphColor = getAminoAcidGlyphColor(b, colorScheme);
          if (!sequtil.baseInRange(indexStart, rStart, rEnd)) {
            indexStart = rStart;
          }
          if (!sequtil.endInRange(indexEnd, rStart, rEnd, rLength)) {
            indexEnd = rEnd;
          }
          return {
            b,
            backgroundColor,
            glyphColor,
            aaIndex,
            indexStart: realToPos(indexStart),
            indexEnd: realEndToPos(indexEnd),
          };
        })
    );
  }
}

export default BaseTranslation;
